import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Col, Row, Table, Button, Modal, Form, Input, Spin, notification, Select } from "antd";
import { PlusOutlined, ApiFilled, SaveFilled } from '@ant-design/icons';

import { getAreas, getAreasById, createArea, updateArea, deleteArea } from "../../../../API/areas";

import AreasSearch from "./components/AreasSearch/AreasSearch";
import Columns from './components/AreaTable/columns';
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";

const { Option } = Select;

const Areas = () => {
  const navigate = useNavigate();
  // Estados componente
  const [api, contextHolder] = notification.useNotification();
  const [areas, setAreas] = useState([]);
  const [controlsArea, setControlsArea] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [form] = Form.useForm();
  // Loader
  const [loading, setLoading] = useState(true);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');
  // Autofocus
  const inputRef = useRef(null);

  useEffect(() => {
    if (isModalOpen && !editingArea) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [isModalOpen, editingArea]);

  // Cargar lista de áreas
  const fetchAreas = async () => {
    setLoading(true);
    try {
      const response = await getAreas();
      setAreas(response);
      setFilteredData(response);
    } catch {
      api.error({ message: "Error al cargar las áreas"});
    } finally {
      setLoading(false);
    }
  };

  // Guardar área (crear o actualizar)
  const handleSave = async (values) => {
    try {
      if (editingArea) {
        await updateArea(editingArea.id, values);
        api.success({ message: "Área actualizada."});
      } else {
        await createArea(values);
        api.success({ message: "Área agregada."});
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingArea(null);
      fetchAreas();
    } catch (error) {
      api.error({ 
        message: "Error al guardar el área.",
        description: error?.response?.data?.name?.[0].includes("already exists") 
          ? "Ya existe un área con ese nombre."
          : "Ocurrió un error inesperado al guardar el área.",
        key: 'save-areas-error'
      });
    }
  };

  // Eliminar un área
  const handleDelete = async (id) => {
    try {
      await deleteArea(id);
      api.success({ message: "Área eliminado."});
      fetchAreas();
    } catch {
      api.error({ message: "Error al eliminar el área."});
    }
  };

  // Mostrar modal para agregar/editar
  const showModal = async (area = null) => {
    if (area) {
      try {
        setLoading(true);
        // llamado al endpoint por code/id
        const fullArea = await getAreasById(area.id);
        setEditingArea(fullArea);
        form.setFieldsValue(fullArea);
      } catch (error) {
        api.error({ message: "Error al cargar datos del área." });
      } finally {
        setLoading(false);
      }
    } else {
      setEditingArea(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const areaTableFilter = () => {
    const uniqueUsers = Array.from(
      new Set(areas.map(a => a.user?.username).filter(Boolean))).map(username => ({ username })); 

    const uniqueAreas = Array.from(
      new Set(areas.map(a => a.area?.name).filter(Boolean))).map(name => ({ name }));
  
    return { 
      users: uniqueUsers, 
      areas: uniqueAreas 
    };
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
    const areas = await getAreasById(record.id);
    setChangeLogData(record);
    setChangeLogLogs(areas.logs || []);
    setChangeLogVisible(true); 
    } catch (error) {
    api.error({ message: "Error al obtener logs" });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
    setChangeLogData(null);
    setChangeLogLogs([]);
    }, 300);
  };
  
  return (
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start" gutter={[0, 16]}>
              <Col span={18}>
                <Button 
                  className="add-button" 
                  onClick={() => showModal()}>
                  <PlusOutlined />
                  Agregar Area
                </Button>
              </Col>
              <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <AreasSearch
                  areasData={areas}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              columns={Columns(showModal, handleDelete, areaTableFilter(), handleOpenChangeLog)} 
              dataSource={filteredData} 
              scroll={{ y: 350 }}
              rowKey="id"
              pagination={{
                defaultPageSize: 5,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
            />
            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </Spin>

          {/* Modal para Agregar/Editar */}
          <Modal
            className="dependency-modal"
            title={
              <span>
                <ApiFilled style={{ marginRight: 8 }} />
                {editingArea ? "Editar Área" : "Agregar Área"}
              </span>
            } 
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onOk={() => form.submit()}
            cancelButtonProps={{ className: 'cancel-button' }}
            okButtonProps={{ className: 'save-type-button' }}
            cancelText="Cancelar"
            okText={
              <span>
                <SaveFilled style={{ marginRight: 6 }} />
                Guardar
              </span>
            }
            footer={[
              <Button key="cancel" className="cancel-button" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>,
              <Button key="save" className="save-type-button" onClick={() => form.submit()} icon={<SaveFilled />}>
                Guardar
              </Button>
            ]}
          >
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item name="name" label="Nombre del Área" rules={[{ required: true, message: "Campo requerido" }]}> 
                <Input.TextArea
                  ref={inputRef}
                  maxLength={50} 
                  showCount
                  style={{ resize: 'none', width: '100%' }} 
                />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </>
  );
};

export default Areas;